import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { useAuthStore } from '../../stores/auth.store';
import { Icon } from '../../components/ui/Icon';
import { ActionsMenu } from '../../components/ui/ActionsMenu';
import { Breadcrumb } from '../../components/ui/Breadcrumb';
import { Chip, statusTone } from '../../components/ui/Chip';
import { DetailSkeleton } from '../../components/ui/Skeleton';
import { EmptyState } from '../../components/ui/EmptyState';
import { Field } from '../../components/ui/Modal';
import { AnimatedNumber } from '../../components/ui/AnimatedNumber';
import { LifecycleStepper, NextActionCard, type LifecycleStep, type LifecycleAction } from '../../components/ui/LifecycleStepper';
import { confirm as confirmDialog } from '../../stores/confirm.store';
import { toast } from '../../stores/toast.store';
import { fmtEur, fmtDate } from '../../lib/utils';
import { BodyRentalStatus, BodyRentalRateUnit } from '@masamune/shared';
import { useIsMobile } from '../../hooks/useIsMobile';
import { BodyRentalMobile } from './BodyRentalMobile';

interface BREngagement {
  id: string; role: string; seniority: string | null; supplierId: string | null;
  supplier: { id: string; fullName: string; email: string | null; phone: string | null; city: string | null } | null;
  status: string; rateUnit: string; clientRate: number; supplierCost: number; quantity: number;
  startDate: string | null; endDate: string | null; supplierPaidAt: string | null; notes: string | null;
  invoices: Array<{ id: string; number: string | null; status: string; total: string; issueDate: string | null; paidDate: string | null }>;
  revenue: number; cost?: number; margin?: number;
}
interface BRDetail {
  id: string; title: string; description: string | null; status: string; neededRoles: number;
  client: { id: string; legalName: string; shortName: string };
  project: { id: string; name: string; code: string } | null;
  engagements: BREngagement[];
  totals: { revenue: number; cost?: number; margin?: number };
}

const RATE_LABEL: Record<string, string> = { HOUR: '/ora', DAY: '/giorno', MONTH: '/mese' };

const REQUEST_FLOW: Array<LifecycleStep & { status: string }> = [
  { key: 'RICHIESTA', status: 'RICHIESTA', label: 'Richiesta', icon: 'brief' },
  { key: 'RICERCA', status: 'RICERCA', label: 'Ricerca', icon: 'search' },
  { key: 'PROPOSTO', status: 'PROPOSTO', label: 'Proposto', icon: 'send' },
  { key: 'ATTIVO', status: 'ATTIVO', label: 'Attivo', icon: 'check' },
  { key: 'TERMINATO', status: 'TERMINATO', label: 'Terminato', icon: 'file' },
];

/**
 * Sul telefono la richiesta è BodyRentalMobile: niente tabella a dieci
 * colonne, una riga per figura e ogni valore si tocca. Stesse chiamate; sul
 * computer resta questa, identica.
 */
export function BodyRentalDetailPage() {
  const isMobile = useIsMobile();
  return isMobile ? <div className="page"><BodyRentalMobile /></div> : <BodyRentalDetailDesktop />;
}

function BodyRentalDetailDesktop() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const showMargin = !!user?.role.canSeeMargin;
  const [adding, setAdding] = useState(false);

  const { data: r, isLoading, isError } = useQuery({
    queryKey: ['body-rental', id],
    queryFn: () => api.get<BRDetail>(`/body-rental/${id}`),
    enabled: !!id,
    retry: false,
  });
  const { data: suppliers } = useQuery({
    queryKey: ['suppliers-lite'],
    queryFn: () => api.get<{ items: Array<{ id: string; fullName: string }> }>('/suppliers?pageSize=200'),
  });

  const invalidate = () => { qc.invalidateQueries({ queryKey: ['body-rental', id] }); qc.invalidateQueries({ queryKey: ['body-rental', 'list'] }); };

  const patchRequest = useMutation({
    mutationFn: (body: any) => api.patch(`/body-rental/${id}`, body),
    onSuccess: invalidate,
  });
  const patchEng = useMutation({
    mutationFn: ({ engId, body }: { engId: string; body: any }) => api.patch(`/body-rental/engagements/${engId}`, body),
    onSuccess: invalidate,
    onError: (e) => toast.alert('Errore', e instanceof ApiError ? e.message : 'Riprova'),
  });
  const addEng = useMutation({
    mutationFn: (body: any) => api.post(`/body-rental/${id}/engagements`, body),
    onSuccess: () => { invalidate(); setAdding(false); toast.ok('Figura aggiunta'); },
    onError: (e) => toast.alert('Errore', e instanceof ApiError ? e.message : 'Riprova'),
  });
  const delEng = useMutation({
    mutationFn: (engId: string) => api.delete(`/body-rental/engagements/${engId}`),
    onSuccess: invalidate,
  });
  const genInvoice = useMutation({
    mutationFn: (engId: string) => api.post<{ id: string }>(`/body-rental/engagements/${engId}/invoice`, {}),
    onSuccess: (inv) => { invalidate(); toast.ok('Fattura creata', 'Bozza pronta da emettere'); navigate(`/invoices/${inv.id}`); },
    onError: (e) => toast.alert('Errore', e instanceof ApiError ? e.message : 'Riprova'),
  });

  if (!id) return <Navigate to="/talent?p=bodyrental" replace />;
  if (isError) return (
    <div className="page">
      <Breadcrumb items={[{ label: 'Body Rental', to: '/talent?p=bodyrental' }, { label: r?.title ?? 'Richiesta' }]} />
      <div className="max-w-[480px] mx-auto py-20 text-center">
        <h1 className="t-h1">Richiesta non trovata</h1>
      </div>
    </div>
  );
  if (isLoading || !r) return <DetailSkeleton header="square" />;

  const isCancelled = r.status === BodyRentalStatus.ANNULLATO;
  const stageIdx = Math.max(0, REQUEST_FLOW.findIndex((f) => f.status === r.status));

  const assigned = r.engagements.filter((e) => e.supplierId).length;

  // Next action: prima azione concreta rilevante.
  const nextAction: LifecycleAction | null = (() => {
    if (isCancelled) return null;
    if (r.engagements.length === 0) {
      return { title: 'Definisci le figure', body: `Aggiungi i professionisti richiesti (${r.neededRoles} previsti).`, cta: 'Aggiungi figura', icon: 'plus', tone: 'primary', onClick: () => setAdding(true) };
    }
    const unassigned = r.engagements.find((e) => !e.supplierId);
    if (unassigned) {
      return { title: 'Assegna i professionisti', body: `${r.engagements.length - assigned} figure ancora senza professionista. Assegnali dalla tabella.`, cta: 'Aggiungi figura', icon: 'users', tone: 'info', onClick: () => setAdding(true) };
    }
    const noInvoice = r.engagements.find((e) => e.supplierId && e.invoices.length === 0);
    if (noInvoice) {
      return { title: 'Genera la fattura', body: `Tutte le figure sono assegnate. Fattura "${noInvoice.role}" al cliente.`, cta: 'Genera fattura', icon: 'invoice', tone: 'primary', onClick: () => genInvoice.mutate(noInvoice.id), loading: genInvoice.isPending };
    }
    const toPay = r.engagements.find((e) => e.supplierId && !e.supplierPaidAt);
    if (toPay) {
      return { title: 'Paga i professionisti', body: `Registra il pagamento a "${toPay.supplier?.fullName ?? toPay.role}".`, cta: 'Segna pagato', icon: 'euro', tone: 'ok', onClick: () => patchEng.mutate({ engId: toPay.id, body: { supplierPaid: true } }), loading: patchEng.isPending };
    }
    if (r.status !== BodyRentalStatus.TERMINATO) {
      return { title: 'Chiudi la richiesta', body: 'Tutte le figure sono fatturate e pagate. Segna la richiesta come terminata.', cta: 'Termina', icon: 'check', tone: 'ok', onClick: () => patchRequest.mutate({ status: BodyRentalStatus.TERMINATO }), loading: patchRequest.isPending };
    }
    return null;
  })();

  // Eliminazione user-friendly: sparizione immediata + "Annulla". La delete è reale
  // (soft-delete lato server) quindi la lista è sempre coerente anche senza cache;
  // l'Annulla ripristina davvero la richiesta. Nessun modale di conferma.
  const handleDelete = () => {
    const title = r.title;
    // rimozione ottimistica dalla lista in cache (sparizione istantanea)
    qc.setQueryData<{ items: Array<{ id: string }> }>(['body-rental', 'list'], (old) =>
      old ? { ...old, items: old.items.filter((i) => i.id !== id) } : old);
    navigate('/talent?p=bodyrental');
    api.delete(`/body-rental/${id}`)
      .then(() => qc.invalidateQueries({ queryKey: ['body-rental'] }))
      .catch(() => { toast.alert('Eliminazione non riuscita', 'Riprova'); qc.invalidateQueries({ queryKey: ['body-rental'] }); });
    toast.undo('Richiesta eliminata', {
      description: title,
      onUndo: () => api.post(`/body-rental/${id}/restore`)
        .then(() => qc.invalidateQueries({ queryKey: ['body-rental'] }))
        .catch(() => toast.alert('Ripristino non riuscito', 'Riprova')),
    });
  };

  return (
    <div className="page">
      <Breadcrumb items={[{ label: 'Body Rental', to: '/talent?p=bodyrental' }, { label: r?.title ?? 'Richiesta' }]} />

      {/* Header editoriale: titolo leggero e grande, hairline aurora dal ::after */}
      <div className="page-ed-hd flex items-start justify-between gap-4">
        <div className="min-w-0 max-w-[760px]">
          {/* L1 protagonista: il titolo (peso leggero, grande), tronca se lungo */}
          <h1 className="ed-title truncate">{r.title}</h1>
          {/* L3 meta-indice: figure richieste·assegnate su UNA riga mute (niente codice interno) */}
          <div className="detail-meta mt-2 truncate">
            <span><span className="v">{r.neededRoles}</span> figure richieste</span>
            <span className="mute-2">·</span>
            <span><span className="v">{assigned}/{r.engagements.length}</span> assegnate</span>
          </div>
          {/* L4 più arretrata: cliente + progetto, label mute, valori --text */}
          <div className="detail-meta mt-1 truncate">
            <span><span className="mute-2 mr-1.5">Cliente</span><span className="v cursor-pointer hover:underline underline-offset-2" onClick={() => navigate(`/clients/${r.client.id}`)}>{r.client.legalName}</span></span>
            {r.project && <span className="mute-2">·</span>}
            {r.project && <span><span className="mute-2 mr-1.5">Progetto</span><span className="v">{r.project.code} {r.project.name}</span></span>}
          </div>
          {/* Stato: riga dedicata SOTTO, unico accento-colore dell'header */}
          <div className="flex gap-1.5 mt-2.5 items-center">
            <Chip tone={statusTone(r.status)}>{r.status}</Chip>
          </div>
        </div>
        <div className="flex gap-2 pb-1">
          <ActionsMenu
            variant="ghost"
            items={[
              {
                key: 'cancel',
                label: 'Annulla richiesta',
                icon: 'x',
                onClick: () => patchRequest.mutate({ status: BodyRentalStatus.ANNULLATO }),
                hidden: isCancelled || r.status === BodyRentalStatus.TERMINATO,
              },
              { key: 'delete', label: 'Elimina', icon: 'trash', danger: true, onClick: handleDelete },
            ]}
          />
        </div>
      </div>

      {/* Numeri NUDI sul foglio, separati da hairline: niente scatole né icone */}
      <div className="stat-band">
        <div>
          <div className="st-label">Figure</div>
          <div className="st-value"><AnimatedNumber value={assigned} /><span className="u">/{r.engagements.length}</span></div>
          <div className="st-delta">assegnate · {r.neededRoles} richieste</div>
        </div>
        <div>
          <div className="st-label">Ricavo</div>
          <div className="st-value"><span className="u">€</span><AnimatedNumber value={r.totals.revenue} format={fmtEur} /></div>
          <div className="st-delta">al cliente</div>
        </div>
        {showMargin && (
          <div>
            <div className="st-label">Costo</div>
            <div className="st-value"><span className="u">€</span><AnimatedNumber value={r.totals.cost ?? 0} format={fmtEur} /></div>
            <div className="st-delta">professionisti</div>
          </div>
        )}
        {showMargin ? (
          <div>
            <div className="st-label">Margine</div>
            <div className="st-value"><span className="u">€</span><AnimatedNumber value={r.totals.margin ?? 0} format={fmtEur} /></div>
            <div className="st-delta">{r.totals.revenue > 0 ? `${(((r.totals.margin ?? 0) / r.totals.revenue) * 100).toFixed(0)}%` : '—'}</div>
          </div>
        ) : (
          <div>
            <div className="st-label">Margine</div>
            <div className="st-value mute-2">•••</div>
            <div className="st-delta">Riservato</div>
          </div>
        )}
      </div>

      {isCancelled ? (
        <div className="card mb-6"><div className="card-bd py-5 flex items-center justify-center gap-3">
          <Chip tone="alert">Richiesta annullata</Chip>
          <button className="btn btn-sm" onClick={() => patchRequest.mutate({ status: BodyRentalStatus.RICHIESTA })}>
            <Icon name="refresh" size={12} /> Riapri richiesta
          </button>
        </div></div>
      ) : (
        <LifecycleStepper
          steps={REQUEST_FLOW}
          currentIdx={stageIdx}
          onStepClick={(_i, step) => {
            const s = (step as LifecycleStep & { status: string }).status;
            if (s !== r.status) patchRequest.mutate({ status: s });
          }}
        />
      )}

      {nextAction && <div className="mb-6"><NextActionCard action={nextAction} /></div>}

      {/* Figure / ingaggi: tabella full-bleed sul foglio, kicker micro + conteggio + CTA */}
      <div className="ed-section">
        <div className="data-hd">
          <span className="t-micro">Figure ingaggiate</span>
          <div className="flex items-center gap-3">
            <span className="mono mute text-[12px]">{r.engagements.length}</span>
            <button className="btn btn-sm btn-primary" onClick={() => setAdding((v) => !v)}>
              <Icon name={adding ? 'x' : 'plus'} size={12} /> {adding ? 'Annulla' : 'Aggiungi figura'}
            </button>
          </div>
        </div>

        {adding && <AddEngagementForm suppliers={suppliers?.items ?? []} showMargin={showMargin} onAdd={(b) => addEng.mutate(b)} pending={addEng.isPending} />}

        <table className="tbl tbl--ed">
          <thead>
            <tr>
              <th>Ruolo</th><th>Professionista</th><th>Tariffa cli.</th>{showMargin && <th>Costo pro.</th>}<th className="num">Q.tà</th>
              {showMargin && <th className="num">Margine</th>}<th>Stato</th><th>Fattura</th><th>Pro. pagato</th><th />
            </tr>
          </thead>
          <tbody>
            {r.engagements.map((e) => {
              const inv = e.invoices[0];
              return (
                <tr key={e.id}>
                  <td>
                    <div className="font-semibold text-[13px]">{e.role}</div>
                    {e.seniority && <div className="mute text-[12px]">{e.seniority}</div>}
                  </td>
                  <td style={{ minWidth: 160 }}>
                    <select className="inp inp-sm" value={e.supplierId ?? ''} onChange={(ev) => patchEng.mutate({ engId: e.id, body: { supplierId: ev.target.value || null, ...(ev.target.value ? { status: e.status === 'RICERCA' ? 'PROPOSTO' : e.status } : {}) } })}>
                      <option value="">— Da assegnare —</option>
                      {(suppliers?.items ?? []).map((s) => <option key={s.id} value={s.id}>{s.fullName}</option>)}
                    </select>
                  </td>
                  <td style={{ width: 130 }}>
                    <div className="flex items-center gap-1">
                      <input className="inp inp-sm mono text-right" style={{ width: 78 }} type="number" defaultValue={e.clientRate}
                        onBlur={(ev) => { const v = Number(ev.target.value); if (v !== e.clientRate) patchEng.mutate({ engId: e.id, body: { clientRate: v } }); }} />
                      <select className="inp inp-sm" style={{ width: 56 }} value={e.rateUnit} onChange={(ev) => patchEng.mutate({ engId: e.id, body: { rateUnit: ev.target.value } })}>
                        {Object.values(BodyRentalRateUnit).map((u) => <option key={u} value={u}>{RATE_LABEL[u]}</option>)}
                      </select>
                    </div>
                  </td>
                  {showMargin && (
                    <td style={{ width: 90 }}>
                      <input className="inp inp-sm mono text-right" type="number" defaultValue={e.supplierCost}
                        onBlur={(ev) => { const v = Number(ev.target.value); if (v !== e.supplierCost) patchEng.mutate({ engId: e.id, body: { supplierCost: v } }); }} />
                    </td>
                  )}
                  <td className="num" style={{ width: 70 }}>
                    <input className="inp inp-sm mono text-right" type="number" defaultValue={e.quantity}
                      onBlur={(ev) => { const v = Number(ev.target.value); if (v !== e.quantity) patchEng.mutate({ engId: e.id, body: { quantity: v } }); }} />
                  </td>
                  {/* Margine = metrica ordinaria → viola, niente semaforo né pill */}
                  {showMargin && <td className="num tbl-metric accent">€ {fmtEur(e.margin ?? 0)}</td>}
                  <td>
                    <select className="inp inp-sm" value={e.status} onChange={(ev) => patchEng.mutate({ engId: e.id, body: { status: ev.target.value } })}>
                      {Object.values(BodyRentalStatus).map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td>
                    {inv ? (
                      <span className="row cursor-pointer hover:text-ink" onClick={() => navigate(`/invoices/${inv.id}`)}>
                        <Chip tone={statusTone(inv.status)}>{inv.number ?? 'bozza'}</Chip>
                      </span>
                    ) : (
                      <button className="btn btn-sm" disabled={!e.supplierId || genInvoice.isPending} onClick={() => genInvoice.mutate(e.id)}>
                        <Icon name="invoice" size={10} /> Genera
                      </button>
                    )}
                  </td>
                  <td>
                    <button className={`btn btn-sm ${e.supplierPaidAt ? 'btn-ok' : ''}`} disabled={!e.supplierId || patchEng.isPending}
                      onClick={() => patchEng.mutate({ engId: e.id, body: { supplierPaid: !e.supplierPaidAt } })}
                      title={e.supplierPaidAt ? `Pagato il ${fmtDate(e.supplierPaidAt)}` : 'Segna il professionista come pagato'}>
                      <Icon name="check" size={10} /> {e.supplierPaidAt ? 'Pagato' : 'Da pagare'}
                    </button>
                  </td>
                  <td className="num">
                    <button className="btn btn-sm btn-icon" title="Elimina figura" onClick={async () => {
                      if (await confirmDialog({ title: 'Rimuovi figura', body: `Rimuovere "${e.role}"?`, danger: true, confirmLabel: 'Rimuovi' })) delEng.mutate(e.id);
                    }}>
                      <Icon name="trash" size={12} />
                    </button>
                  </td>
                </tr>
              );
            })}
            {!r.engagements.length && !adding && (
              <tr><td colSpan={showMargin ? 10 : 8} style={{ padding: 0 }}>
                <EmptyState
                  icon="users"
                  title="Nessuna figura ingaggiata"
                  description="Aggiungi i professionisti richiesti dal pulsante «Aggiungi figura»."
                />
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {r.description && (
        <>
          <div className="ed-divider" />
          {/* Note: kicker-block nudo sul foglio, niente card */}
          <div>
            <div className="data-hd"><span className="t-micro">Note richiesta</span></div>
            <div className="text-[13px] whitespace-pre-wrap">{r.description}</div>
          </div>
        </>
      )}
    </div>
  );
}

function AddEngagementForm({ suppliers, showMargin, onAdd, pending }: {
  suppliers: Array<{ id: string; fullName: string }>;
  showMargin: boolean;
  onAdd: (body: any) => void;
  pending: boolean;
}) {
  const [f, setF] = useState({ role: '', seniority: '', supplierId: '', rateUnit: 'DAY', clientRate: 0, supplierCost: 0, quantity: 1 });
  return (
    // Il composer resta su card: gli input hanno bisogno del foglio
    <div className="card card-bd mb-4" style={{ background: 'var(--paper-2)' }}>
      <div className="form-grid">
        <Field label="Ruolo" required span={4}>
          <input className="inp" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} placeholder="Es. Sviluppatore React" />
        </Field>
        <Field label="Seniority" span={3}>
          <input className="inp" value={f.seniority} onChange={(e) => setF({ ...f, seniority: e.target.value })} placeholder="Senior" />
        </Field>
        <Field label="Professionista" span={5}>
          <select className="inp" value={f.supplierId} onChange={(e) => setF({ ...f, supplierId: e.target.value })}>
            <option value="">— Da assegnare —</option>
            {suppliers.map((s) => <option key={s.id} value={s.id}>{s.fullName}</option>)}
          </select>
        </Field>
        <Field label="Tariffa cliente" span={3}>
          <input className="inp mono text-right" type="number" value={f.clientRate} onChange={(e) => setF({ ...f, clientRate: Number(e.target.value) })} />
        </Field>
        <Field label="Unità" span={3}>
          <select className="inp" value={f.rateUnit} onChange={(e) => setF({ ...f, rateUnit: e.target.value })}>
            {Object.values(BodyRentalRateUnit).map((u) => <option key={u} value={u}>{RATE_LABEL[u]}</option>)}
          </select>
        </Field>
        {showMargin && (
          <Field label="Costo professionista" span={3}>
            <input className="inp mono text-right" type="number" value={f.supplierCost} onChange={(e) => setF({ ...f, supplierCost: Number(e.target.value) })} />
          </Field>
        )}
        <Field label="Quantità" span={3}>
          <input className="inp mono text-right" type="number" value={f.quantity} onChange={(e) => setF({ ...f, quantity: Number(e.target.value) })} />
        </Field>
      </div>
      <div className="flex justify-end mt-4">
        <button className="btn btn-primary" disabled={!f.role || pending}
          onClick={() => onAdd({ ...f, supplierId: f.supplierId || undefined })}>
          <Icon name="plus" size={12} /> Aggiungi figura
        </button>
      </div>
    </div>
  );
}
